import api from './client';

// ─── Types ───────────────────────────────────────────────────────────

export type RelationshipRole =
  | 'dominant' | 'submissive' | 'switch'
  | 'master' | 'slave' | 'owned'
  | 'daddy_dom' | 'little' | 'brat'
  | 'primal' | 'pet' | 'rigger' | 'rope_bunny'
  | 'exploring' | 'kink_curious';

export interface DiscoveryProfile {
  userId: string;
  displayName: string;
  age?: number;
  /** Free-varchar(20), no CHECK constraint — always in the public allowlist (discoveryProfileService.ts getPublicProfile). */
  gender?: string;
  bio?: string;
  photos: DiscoveryPhoto[];
  interests?: string[];
  lifestyleTags?: string[];
  orientation?: string;
  relationshipGoal?: string;
  relationshipRole?: RelationshipRole | null;
  /** `height_cm` column, camelCased by the response middleware — was never `height` on the wire. */
  heightCm?: number;
  location?: { lat: number; lng: number };
  city?: string;
  distance?: number;
  isOnline?: boolean;
  isVerified?: boolean;
  compatibilityScore?: number;
  /** Generic compatibility reasons — never specific activities or limits */
  compatibilityReasons?: string[];
  experienceLevel?: string;
  lookingFor?: string[];
  limitsVisibility?: 'private' | 'matches_only' | 'public';
  lastActive?: string;
  /** Whether this user has mutually matched with the viewer */
  isMutualMatch?: boolean;
}

/**
 * DiscoverySelfProfile — the caller's OWN profile, `GET /discovery/profile`
 * (`discoveryProfileService.getMyProfile`, a raw `SELECT * FROM
 * discovery.profiles`). A strict superset of `DiscoveryProfile`: besides the
 * fields anyone browsing can see, it also carries this user's own privacy
 * toggles and preferences — columns `getPublicProfile`'s explicit allowlist
 * deliberately never returns to another viewer (see that function's S9
 * security comment). Used by CultivateProfile.tsx / CultivateSettings.tsx
 * via useDiscoveryProfileSelfQuery(); never use this type for another user's
 * profile.
 */
export interface DiscoverySelfProfile extends DiscoveryProfile {
  incognitoMode?: boolean;
  showOnlineStatus?: boolean;
  showDistance?: boolean;
  showAge?: boolean;
  showGender?: boolean;
  showOrientation?: boolean;
  showRelationshipRole?: boolean;
  showInterests?: boolean;
  showLifestyleTags?: boolean;
  showCompatibilityScore?: boolean;
  /** Viewer-side "see compatibility scores while browsing" preference — not gated by another user's settings. */
  seeCompatibilityScores?: boolean;
  notificationPrefs?: {
    newMessages?: boolean;
    newMatches?: boolean;
    profileViews?: boolean;
  };
}

export interface DiscoveryPhoto {
  id: string;
  url: string;
  isPrimary?: boolean;
  order?: number;
}

export interface DiscoveryMatch {
  /** Backend returns these from getMutualMatches (camelCase middleware) */
  userId: number;
  displayName: string;
  age?: number;
  thumbnail?: string;
  mutualAt: string;
  conversationId?: string;
  /**
   * One of the two `discovery.matches` direction rows for this pair — valid
   * as a `graduationProposals.create()` argument, but NOT a stable per-pair
   * key (the two rows can be returned to the two different users
   * arbitrarily). Never compare this to another user's matchId.
   */
  matchId?: string;
  photo?: string;
  matchedAt?: string;
  lastMessage?: string;
  unreadCount?: number;
  // ── Spec 293 Package C — graduation-proposal state (batched, pair-scoped) ──
  pendingProposal?: GraduationPendingProposal | null;
  coolOffActive?: boolean;
  hasGraduated?: boolean;
  graduatedOrganizationPublicId?: string | null;
}

// ─── Graduation Proposals (Spec 293 Package C) ──────────────────────

export type GraduationProposalRole = 'dominant' | 'submissive' | 'equal';
export type GraduationProposalStatus = 'pending' | 'accepted' | 'declined' | 'withdrawn' | 'expired';

export interface GraduationPendingProposal {
  publicId: string;
  /** True when the current viewer sent this proposal. */
  isInitiator: boolean;
  /** The initiator's role pick — the only role known while status is pending. */
  initiatorRole: GraduationProposalRole;
  message: string | null;
  expiresAt: string;
}

export interface GraduationProposalResult {
  id: number;
  publicId: string;
  matchId: string;
  initiatorRole: GraduationProposalRole;
  partnerRole: GraduationProposalRole | null;
  message: string | null;
  status: GraduationProposalStatus;
  expiresAt: string;
  createdAt: string;
  /** Only present on a successful accept response. */
  organizationPublicId?: string;
}

export interface DiscoveryConversation {
  id: string;
  matchId?: string;
  otherUser: {
    userId: number;
    displayName: string | null;
    thumbnail: string | null;
  };
  lastMessage: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  /** Flattened convenience fields (populated by frontend mapping) */
  displayName?: string;
  photo?: string;
}

export interface DiscoveryMessage {
  id: string;
  conversationId: string;
  senderId: number;
  content: string;
  contentType: 'text' | 'image';
  /** REST uses createdAt (from DB), camelCase middleware transforms created_at */
  createdAt: string;
  /** True when the message was sent by the requesting user */
  isOwn?: boolean;
}

export interface DiscoveryAlbum {
  id: string;
  name: string;
  description?: string;
  photoCount: number;
  createdAt: string;
}

export interface DiscoveryFeedParams {
  lat?: number;
  lng?: number;
  radius?: number;
  ageMin?: number;
  ageMax?: number;
  onlineOnly?: boolean;
  verifiedOnly?: boolean;
  hasPhotos?: boolean;
  includeTags?: string[];
  excludeTags?: string[];
  roles?: RelationshipRole[];
  excludeRoles?: RelationshipRole[];
  orientations?: string[];
  excludeOrientations?: string[];
  goals?: string[];
  excludeGoals?: string[];
  cursor?: string;
  /** H7 — feed ordering. Backend defaults to 'best_match' when omitted. */
  sort?: 'best_match' | 'newest' | 'nearby' | 'recently_active';
}

/**
 * DiscoveryFeedRow — the RAW shape `GET /discovery/feed` actually returns per
 * profile (post camelCase middleware), straight from discoveryService.ts's
 * DiscoveryResult. This is NOT what CultivateProfileCard consumes — see
 * `mapFeedRowToProfile` below (H6), which adapts this onto `DiscoveryProfile`
 * at the API-client boundary so the card component never has to know about
 * the backend's real field names.
 */
export interface DiscoveryFeedRow {
  userId: number;
  displayName: string;
  age: number;
  gender: string;
  bio: string | null;
  relationshipRole: RelationshipRole | null;
  /** Pre-formatted, deliberately imprecise distance string, e.g. "~3 km" — NOT a number, NOT miles. */
  distance: string | null;
  /** Distance in meters, rounded to the nearest 500m for privacy. */
  distanceMeters: number | null;
  isOnline: boolean;
  phoneVerified: boolean;
  selfieVerified: boolean;
  primaryPhoto: string | null;
  thumbnail: string | null;
  lifestyleTags: string[];
  compatibilityScore: number | null;
  compatibilityReasons: string[];
}

const METERS_PER_MILE = 1609.34;

/**
 * H6 — maps a raw feed row (backend's actual field names/units) onto the
 * `DiscoveryProfile` shape CultivateProfileCard expects (photos[], isVerified,
 * interests, distance in miles). Keeps the card component itself untouched —
 * the adaptation lives here, at the API-client boundary, same principle as
 * the C1 envelope fix.
 */
export function mapFeedRowToProfile(row: DiscoveryFeedRow): DiscoveryProfile {
  const photoUrl = row.primaryPhoto || row.thumbnail;
  const photos: DiscoveryPhoto[] = photoUrl
    ? [{ id: `${row.userId}-primary`, url: photoUrl, isPrimary: true }]
    : [];

  return {
    userId: String(row.userId),
    displayName: row.displayName,
    age: row.age,
    bio: row.bio ?? undefined,
    photos,
    interests: row.lifestyleTags || [],
    lifestyleTags: row.lifestyleTags || [],
    relationshipRole: row.relationshipRole,
    distance: row.distanceMeters != null ? row.distanceMeters / METERS_PER_MILE : undefined,
    isOnline: row.isOnline,
    isVerified: row.phoneVerified || row.selfieVerified,
    compatibilityScore: row.compatibilityScore ?? undefined,
    compatibilityReasons: row.compatibilityReasons,
  };
}

export interface PrivacySettings {
  show_online_status?: boolean;
  show_distance?: boolean;
  show_age?: boolean;
  show_gender?: boolean;
  show_orientation?: boolean;
  show_height?: boolean;
  show_relationship_goal?: boolean;
  show_relationship_role?: boolean;
  show_interests?: boolean;
  show_lifestyle_tags?: boolean;
  visibility?: string;
  /**
   * Spec 321-A2. Selects the geohash cell size the STORED location is snapped to
   * at write time — it is not a display toggle. 'approximate' is the default
   * because the private option should be the default, not an opt-in.
   */
  location_precision?: 'exact' | 'precise' | 'approximate' | 'city';
  show_compatibility_score?: boolean;
  see_compatibility_scores?: boolean;
}

export interface NotificationPreferences {
  new_messages?: boolean;
  new_matches?: boolean;
  profile_views?: boolean;
}

// ─── API Client ──────────────────────────────────────────────────────

type ApiResponse<T> = { success: boolean; data: T };

/** Convert array params to comma-separated strings for backend compatibility */
function serializeFeedParams(params?: DiscoveryFeedParams): Record<string, string | number | boolean | undefined> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    out[key] = Array.isArray(value) ? value.join(',') : value;
  }
  return out;
}

export const discoveryAPI = {
  // ── Feed & Grid ──────────────────────────────────────────────────

  getFeed: (params?: DiscoveryFeedParams) =>
    api.get<ApiResponse<{ profiles: DiscoveryFeedRow[]; nextCursor: string | null; hasMore: boolean }>>('/discovery/feed', { params: serializeFeedParams(params) }),

  getGrid: (params?: Omit<DiscoveryFeedParams, 'cursor'>) =>
    api.get<ApiResponse<DiscoveryProfile[]>>('/discovery/grid', { params: serializeFeedParams(params) }),

  // ── Matching ─────────────────────────────────────────────────────

  likeUser: (userId: string) =>
    api.post<ApiResponse<{ isMutual: boolean; conversationId?: string }>>(`/discovery/${userId}/like`),

  passUser: (userId: string) =>
    api.post<ApiResponse<Record<string, unknown>>>(`/discovery/${userId}/pass`),

  getMatches: () =>
    api.get<ApiResponse<DiscoveryMatch[]>>('/discovery/matches'),

  unmatch: (userId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/matches/${userId}`),

  // ── Graduation Proposals (Spec 293 Package C) ───────────────────────
  // Bilateral consent, replaces the deprecated (410) single-call graduate
  // endpoint above's dead client method. Backend: graduationProposalService.ts.

  graduationProposals: {
    create: (matchId: string, role: GraduationProposalRole, message?: string) =>
      api.post<ApiResponse<GraduationProposalResult>>('/discovery/graduation-proposals', { matchId, role, message }),

    respond: (proposalPublicId: string, role: GraduationProposalRole, accept: boolean) =>
      api.post<ApiResponse<GraduationProposalResult>>(
        `/discovery/graduation-proposals/${proposalPublicId}/respond`,
        { role, accept }
      ),

    withdraw: (proposalPublicId: string) =>
      api.post<ApiResponse<Record<string, unknown>>>(`/discovery/graduation-proposals/${proposalPublicId}/withdraw`),

    list: () =>
      api.get<ApiResponse<GraduationProposalResult[]>>('/discovery/graduation-proposals'),
  },

  // ── Profile (Own) ────────────────────────────────────────────────

  getProfile: () =>
    api.get<ApiResponse<DiscoverySelfProfile>>('/discovery/profile'),

  updateProfile: (data: Partial<DiscoverySelfProfile>) =>
    api.put<ApiResponse<DiscoverySelfProfile>>('/discovery/profile', data),

  deleteProfile: () =>
    api.delete<ApiResponse<Record<string, unknown>>>('/discovery/profile'),

  uploadProfilePhoto: (formData: FormData) =>
    api.post<ApiResponse<DiscoveryPhoto>>('/discovery/profile/photos', formData),

  deleteProfilePhoto: (photoId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/profile/photos/${photoId}`),

  // ── Profile (View Others) ────────────────────────────────────────

  getPublicProfile: (userId: string) =>
    api.get<ApiResponse<DiscoveryProfile>>(`/discovery/profile/${userId}`),

  // ── Settings ─────────────────────────────────────────────────────

  getActiveState: () =>
    api.get<ApiResponse<{ hasProfile: boolean; isActive: boolean }>>('/discovery/settings/active'),

  setActive: (active: boolean) =>
    api.put<ApiResponse<Record<string, unknown>>>('/discovery/settings/active', { active }),

  updatePrivacy: (settings: PrivacySettings) =>
    api.put<ApiResponse<Record<string, unknown>>>('/discovery/settings/privacy', settings),

  updateNotifications: (prefs: NotificationPreferences) =>
    api.put<ApiResponse<Record<string, unknown>>>('/discovery/settings/notifications', prefs),

  toggleIncognito: (enabled: boolean) =>
    api.put<ApiResponse<Record<string, unknown>>>('/discovery/settings/incognito', { enabled }),

  // ── Messaging ────────────────────────────────────────────────────

  getConversations: () =>
    api.get<ApiResponse<DiscoveryConversation[]>>('/discovery/messages/conversations'),

  // Spec 293 Package A1: the actual route (discoveryMessagingRoutes.ts's
  // GET /conversations/:id/messages) returns { data: messages, nextCursor,
  // hasMore } via discoveryMessageService.getMessages()'s PaginatedResult —
  // not the previous (wrong) { messages, nextCursor } shape, which had no
  // hasMore field at all for "Load older messages" to read.
  getMessages: (conversationId: string, cursor?: string) =>
    api.get<ApiResponse<{ data: DiscoveryMessage[]; nextCursor: string | null; hasMore: boolean }>>(
      `/discovery/messages/conversations/${conversationId}/messages`,
      { params: cursor ? { cursor } : undefined }
    ),

  sendMessage: (conversationId: string, content: string, contentType: 'text' | 'image' = 'text') =>
    api.post<ApiResponse<DiscoveryMessage>>(
      `/discovery/messages/conversations/${conversationId}/messages`,
      { content, contentType }
    ),

  markConversationRead: (conversationId: string) =>
    api.put<ApiResponse<Record<string, unknown>>>(
      `/discovery/messages/conversations/${conversationId}/read`
    ),

  deleteMessage: (messageId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/messages/${messageId}`),

  // ── Albums ───────────────────────────────────────────────────────

  createAlbum: (data: { name: string; description?: string }) =>
    api.post<ApiResponse<DiscoveryAlbum>>('/discovery/albums', data),

  getAlbums: () =>
    api.get<ApiResponse<DiscoveryAlbum[]>>('/discovery/albums'),

  updateAlbum: (id: string, data: { name?: string; description?: string }) =>
    api.put<ApiResponse<DiscoveryAlbum>>(`/discovery/albums/${id}`, data),

  deleteAlbum: (id: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/albums/${id}`),

  getAlbumPhotos: (id: string) =>
    api.get<ApiResponse<DiscoveryPhoto[]>>(`/discovery/albums/${id}/photos`),

  uploadAlbumPhoto: (id: string, formData: FormData) =>
    api.post<ApiResponse<DiscoveryPhoto>>(`/discovery/albums/${id}/photos`, formData),

  deleteAlbumPhoto: (albumId: string, photoId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/albums/${albumId}/photos/${photoId}`),

  grantAlbumAccess: (albumId: string, userId: string) =>
    api.post<ApiResponse<Record<string, unknown>>>(`/discovery/albums/${albumId}/access`, { targetUserId: userId }),

  revokeAlbumAccess: (albumId: string, userId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/albums/${albumId}/access/${userId}`),

  getAlbumAccessList: (albumId: string) =>
    api.get<ApiResponse<{ userId: string; displayName: string; grantedAt: string }[]>>(
      `/discovery/albums/${albumId}/access`
    ),

  // ── Safety ───────────────────────────────────────────────────────

  blockUser: (userId: string) =>
    api.post<ApiResponse<Record<string, unknown>>>(`/discovery/safety/block/${userId}`),

  unblockUser: (userId: string) =>
    api.delete<ApiResponse<Record<string, unknown>>>(`/discovery/safety/block/${userId}`),

  getBlockedUsers: () =>
    api.get<ApiResponse<{ userId: string; displayName: string; blockedAt: string }[]>>(
      '/discovery/safety/blocks'
    ),

  reportUser: (data: { reportedUserId: string; category: string; description: string; evidence?: string }) =>
    api.post<ApiResponse<Record<string, unknown>>>('/discovery/safety/report', data),
};
